import React, { useState } from "react";
import { Loader2, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/typed";

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: React.ReactNode }) {
  return (
    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-[#1A2536]">{title}</h1>
        {subtitle && <p className="mt-1.5 text-[15px] text-[#64748B]">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, icon, tone = "navy", testId, hint }: { label: string; value: React.ReactNode; icon: React.ReactNode; tone?: string; testId: string; hint?: string }) {
  const tones: Record<string, string> = {
    navy: "bg-[#1B365D]/5 text-[#1B365D]",
    amber: "bg-[#FEF3C7] text-[#B45309]",
    teal: "bg-[#CCFBF1] text-[#0F766E]",
    red: "bg-[#FEE2E2] text-[#B91C1C]",
    green: "bg-[#DCFCE7] text-[#15803D]",
  };
  return (
    <div className="card-soft p-6 transition-transform duration-300 hover:-translate-y-0.5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <p className="text-sm font-semibold text-[#64748B]">{label}</p>
        <span className={`grid h-10 w-10 place-items-center rounded-xl ${tones[tone]}`}>{icon}</span>
      </div>
      <p className="mt-3 font-mono text-3xl font-semibold tracking-tight text-[#0F172A]">{value}</p>
      {hint && <p className="mt-1 text-xs text-[#94A3B8]">{hint}</p>}
    </div>
  );
}

type CardProps = { title?: string; actions?: React.ReactNode; children: React.ReactNode; className?: string; testId?: string; collapsible?: boolean; defaultOpen?: boolean; badge?: string; badgeTone?: "amber" | "red" | "navy" };

export function Card({ title, actions, children, className = "", testId, collapsible = false, defaultOpen = true, badge, badgeTone = "amber" }: CardProps) {
  const key = `card-open:${testId}`;
  const [open, setOpen] = useState(() => { const v = collapsible ? localStorage.getItem(key) : null; return v === null ? defaultOpen : v === "1"; });
  const toggle = () => { setOpen(!open); localStorage.setItem(key, open ? "0" : "1"); };
  const shown = !collapsible || open;
  return (
    <section className={`card-soft min-w-0 overflow-x-auto ${className}`} data-testid={testId}>
      {title && (
        <div className={`flex flex-wrap items-center justify-between gap-3 px-6 py-4 ${shown ? "border-b border-slate-100" : ""}`}>
          {collapsible ? (
            <button type="button" onClick={toggle} aria-expanded={open} className="group -my-2 flex min-h-[44px] min-w-0 flex-1 items-center gap-2 py-2 text-left" data-testid={testId ? `${testId}-toggle` : undefined}>
              <ChevronDown className={`h-5 w-5 shrink-0 text-[#94A3B8] transition-transform duration-200 group-hover:text-[#1B365D] ${open ? "" : "-rotate-90"}`} />
              <h2 className="text-lg font-bold text-[#1A2536]">{title}</h2>
              {!open && badge && <span className={`ml-1 shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${{ amber: "bg-[#FEF3C7] text-[#B45309]", red: "bg-[#FEE2E2] text-[#B91C1C]", navy: "bg-[#1B365D]/10 text-[#1B365D]" }[badgeTone]}`} data-testid={testId ? `${testId}-badge` : undefined}>{badge}</span>}
            </button>
          ) : <h2 className="text-lg font-bold text-[#1A2536]">{title}</h2>}
          {shown && actions}
        </div>
      )}
      {shown && children}
    </section>
  );
}

export function Empty({ text }: { text: string }) {
  return <p className="px-6 py-12 text-center text-[15px] text-[#94A3B8]">{text}</p>;
}

type ConfirmProps = {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description?: string;
  confirmLabel: string;
  onConfirm: (note: string) => Promise<void>;
  withNote?: boolean;
  danger?: boolean;
  testId: string;
};

export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel, onConfirm, withNote, danger, testId }: ConfirmProps) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await onConfirm(note);
      setNote("");
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{title}</DialogTitle>
          {description && <DialogDescription className="text-[15px]">{description}</DialogDescription>}
        </DialogHeader>
        {withNote && <textarea data-testid={`${testId}-note`} className="field-input h-auto py-3" rows={3} placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />}
        <DialogFooter className="gap-2">
          <button className="btn-secondary btn-sm" onClick={() => onOpenChange(false)} data-testid={`${testId}-cancel`}>Cancel</button>
          <button className={`btn-primary btn-sm ${danger ? "bg-[#B91C1C] hover:bg-[#991B1B]" : ""}`} disabled={busy} onClick={go} data-testid={`${testId}-confirm`}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
